<template>
  <div class="register-page connections-page">
    <div class="row align-items-center header">
      <div class="col-lg-6 d-flex align-items-center">
        <PageTitle :titles="['Conexões']" :data-count="filteredDevices.length" />
      </div>
      <div class="col-lg-6 d-flex justify-content-end align-items-center flex-wrap filters">
        <b-form-select
          v-model="modelFilter"
          :options="modelOptions"
          size="sm"
          class="model-filter mr-3 mb-2 mb-lg-0"
        />
        <b-form-checkbox v-model="hideEmpty" class="mr-3 mb-2 mb-lg-0">
          Ocultar dispositivos sem conexões
        </b-form-checkbox>
        <b-form-checkbox v-model="inRouteOnly" class="mr-3 mb-2 mb-lg-0">
          Apenas em rota
        </b-form-checkbox>
        <b-form-input
          v-model.number="minActuators"
          type="number"
          min="0"
          size="sm"
          placeholder="Mín. atuadores"
          class="min-count-filter mr-3 mb-2 mb-lg-0"
        />
        <b-form-input
          v-model.number="minSensors"
          type="number"
          min="0"
          size="sm"
          placeholder="Mín. sensores"
          class="min-count-filter mr-3 mb-2 mb-lg-0"
        />
        <b-form-checkbox v-model="expandAll" switch class="mb-2 mb-lg-0">
          Expandir todos
        </b-form-checkbox>
      </div>
    </div>

    <div class="register-main">
      <div class="row mt-2 align-items-center">
        <div class="col-lg-4 col-xs-12 mb-2 mb-lg-0">
          <b-input-group size="sm">
            <b-input-group-prepend>
              <b-input-group-text>
                <search-icon />
              </b-input-group-text>
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              placeholder="Buscar por dispositivo, ADDR, modelo ou equipamento conectado..."
            />
            <b-input-group-append v-if="searchQuery">
              <b-button variant="outline-secondary" @click="searchQuery = ''">
                <close-icon />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div class="col-lg-8 col-xs-12 d-flex justify-content-lg-end">
          <b-button
            size="sm"
            variant="outline-3v3"
            class="mr-2"
            :disabled="!filteredDevices.length"
            @click="exportSpreadsheet('csv')"
          >
            <csv-icon style="font-size: 1.1em; vertical-align: -3%" />
            Baixar CSV
          </b-button>
          <b-button
            size="sm"
            variant="outline-3v3"
            :disabled="!filteredDevices.length"
            @click="exportSpreadsheet('xlsx')"
          >
            <excel-icon style="font-size: 1.1em; vertical-align: -3%" />
            Baixar planilha
          </b-button>
        </div>
      </div>

      <div v-if="loading" class="text-muted mt-3">Carregando dispositivos...</div>
      <div v-else-if="filteredDevices.length === 0" class="text-muted mt-3">
        Nenhum dispositivo encontrado com esse filtro.
      </div>

      <div v-else class="row mt-3">
        <div class="col-lg-12">
          <DeviceConnectionDiagram
            v-for="device in filteredDevices"
            :key="device._key"
            :device="device"
            :is-mtd="device._isMtd"
            :expanded="isExpanded(device._key)"
            @toggle="toggleDevice(device._key)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tabulator from "tabulator-tables";
import PageTitle from "@/pages/temps/components/PageTitle";
import DeviceConnectionDiagram from "./components/DeviceConnectionDiagram.vue";
import { isDeviceEmpty, deviceSearchText, buildExportRows, countBySlotKind } from "./deviceSlots.js";
import { dirs, mtd, routes } from "@/api";

export default {
  name: "Connections",
  components: { PageTitle, DeviceConnectionDiagram },
  props: {
    // Vem de App.vue -> router-view (:local="localName"), origem
    // SETTINGS_POSITION.NAME (map.positionInfo()) -- mesmo dado que
    // routes.js já usa pra nomear a estação (ex. "DIR-1-Fazenda-Teste").
    local: [String],
  },
  data() {
    return {
      loading: true,
      devices: [],
      // Estado de expansão por dispositivo (chave -> bool), independente de
      // "expandAll" -- permite que o usuário abra/feche itens individualmente
      // mesmo depois de usar o toggle "Expandir todos".
      expandedMap: {},
      expandAll: false,
      modelFilter: null,
      hideEmpty: true,
      inRouteOnly: false,
      // "" (não number) enquanto o campo estiver vazio -- v-model.number só
      // converte pra Number quando o texto digitado é um número válido.
      minActuators: "",
      minSensors: "",
      routeAddrs: new Set(),
      searchQuery: "",
    };
  },
  computed: {
    // Nome de arquivo só pode ter caracteres seguros -- SETTINGS_POSITION.NAME
    // costuma ter espaço/acento (ex. "Fazenda Teste"), então normaliza pra
    // "fazenda_teste".
    exportFilenameSuffix() {
      return (this.local || "sem-nome")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .toLowerCase();
    },
    modelOptions() {
      const models = [...new Set(this.devices.map((device) => device.MODEL_NAME))].sort();
      return [
        { value: null, text: "Todos os modelos" },
        ...models.map((model) => ({ value: model, text: model })),
      ];
    },
    filteredDevices() {
      const query = this.searchQuery.trim().toLowerCase();
      return this.devices.filter((device) => {
        if (this.modelFilter && device.MODEL_NAME !== this.modelFilter) return false;
        if (this.hideEmpty && isDeviceEmpty(device, device._isMtd)) return false;
        if (this.inRouteOnly && !this.routeAddrs.has(device.ADDR)) return false;
        if (query && !deviceSearchText(device, device._isMtd).includes(query)) return false;
        if (this.minActuators !== "" || this.minSensors !== "") {
          const counts = countBySlotKind(device, device._isMtd);
          if (this.minActuators !== "" && counts.actuators < this.minActuators) return false;
          if (this.minSensors !== "" && counts.sensors < this.minSensors) return false;
        }
        return true;
      });
    },
  },
  watch: {
    // "Expandir todos" é uma ação de estado (não uma trava): força todos os
    // itens filtrados pro mesmo estado quando alternado, mas o usuário ainda
    // pode abrir/fechar itens individualmente depois -- só não persiste até o
    // próximo toggle do switch.
    expandAll(value) {
      const map = {};
      this.filteredDevices.forEach((device) => {
        map[device._key] = value;
      });
      this.expandedMap = map;
    },
    filteredDevices(list) {
      const map = { ...this.expandedMap };
      list.forEach((device) => {
        if (!(device._key in map)) map[device._key] = this.expandAll;
      });
      this.expandedMap = map;
    },
  },
  created() {
    this.$emit("title-change", "Conexões");
    this.load();
  },
  methods: {
    isExpanded(key) {
      return !!this.expandedMap[key];
    },
    toggleDevice(key) {
      this.$set(this.expandedMap, key, !this.expandedMap[key]);
    },
    load() {
      this.loading = true;
      return Promise.all([
        dirs.list().catch(() => []),
        mtd.list().catch(() => []),
        routes.listPerAddr().catch(() => []),
      ])
        .then(([dirList, mtdList, routeList]) => {
          const dirDevices = (dirList || []).map((device) => ({
            ...device,
            _key: `dir-${device.ID}`,
            _isMtd: false,
          }));
          const mtdDevices = (mtdList || []).map((device) => ({
            ...device,
            _key: `mtd-${device.ID}`,
            _isMtd: true,
          }));
          this.devices = [...dirDevices, ...mtdDevices];
          // Mesmo critério de "está em rota" que Dirs/View.vue:isDirsInRoutes
          // já usa (ícone source-branch-icon): o dispositivo tem sua própria
          // linha em ROUTE (ADDR -> NEIGHBOR, próximo salto RF).
          this.routeAddrs = new Set((routeList || []).map((route) => route.ADDR));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Achata os dispositivos filtrados (independente do estado expandido/
    // colapsado de cada um) numa linha por porta ocupada, e baixa via
    // Tabulator -- mesma técnica de tabela off-screen que
    // TableEvents.vue:runDownload já usa (a lib xlsx já está carregada
    // globalmente em public/index.html, sem dependência nova; CSV é nativo
    // do Tabulator, não precisa de lib extra).
    exportSpreadsheet(type) {
      const rows = buildExportRows(this.filteredDevices);
      const columns = [
        { title: "Dispositivo", field: "DISPOSITIVO" },
        { title: "ADDR", field: "ADDR" },
        { title: "Modelo", field: "MODELO" },
        { title: "Grupo", field: "GRUPO" },
        { title: "Porta", field: "PORTA" },
        { title: "Equipamento", field: "EQUIPAMENTO" },
      ];

      const el = document.createElement("div");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);

      let tempTabulator;
      new Promise((resolve) => {
        tempTabulator = new Tabulator(el, {
          data: rows,
          columns,
          tableBuilt: () => resolve(),
        });
      }).then(() => {
        const filename = `conexoes_${this.exportFilenameSuffix}.${type}`;
        if (type === "csv") {
          tempTabulator.download("csv", filename, { bom: true });
        } else {
          tempTabulator.download("xlsx", filename, { sheetName: "Conexões" });
        }
        tempTabulator.destroy();
        el.remove();
      });
    },
  },
};
</script>

<style scoped>
.model-filter {
  width: auto;
  min-width: 160px;
}

.min-count-filter {
  width: auto;
  min-width: 130px;
}
</style>
